import { DataTable } from "../../../../../../../../components/DataTable";
import * as React from "react";
import { Box, Text, useDisclosure, useToast } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import {
  useGetSupplierEngagementReports
} from "../../../../../../../../hooks/query/reports";
import { QUESTIONNAIRE_STATUSES, showErrorToast } from "../../../../../../../../utils";
import { SUPPLIER_QUESTIONNAIRE_CATEGORIES } from "../../../../../../../SupplierQuestionnaire/helpers";
import moment from "moment";
import { StatusBadge } from "./index";
import { SuppliersDataModal } from "../../index";

function SupplierEngagementGrid({ categoryId }) {
  const { t } = useTranslation("translation", {
    keyPrefix: "reportsPage.suppliersData.supplierEngagement.supplierEngagementGrid"
  });
  const toast = useToast();
  const [reportsData, setReportsData] = useState([]);
  const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState("");

  const {
    isOpen: isSuppliersQuestionnaireOpen,
    onOpen: onSuppliersQuestionnaireOpen,
    onClose: onSuppliersQuestionnaireClose
  } = useDisclosure();

  const closeSuppliersQuestionnaireModal = () => {
    setSelectedQuestionnaireId("");
    onSuppliersQuestionnaireClose();
  };



  const {
    isFetching: isReportsDataFetching
  } = useGetSupplierEngagementReports(categoryId, null, {
    onSuccess: (data) => {
      const mappedData = [];

      if (data && Object.keys(data)?.length > 0) {
        Object.keys(data).sort().forEach(supplier => {
          if (Object.keys(data[supplier])?.length > 0) {
            Object.keys(data[supplier]).sort().forEach(username => {
              const foundDecarbQuestionnaire = data[supplier][username].find(
                questionnaire => questionnaire.type === SUPPLIER_QUESTIONNAIRE_CATEGORIES.DECARBONIZATION
              );

              const objToPush = {
                supplier,
                username,
                decarbPlan: typeof foundDecarbQuestionnaire !== "undefined" ?
                  getStatusLabel(foundDecarbQuestionnaire?.status, foundDecarbQuestionnaire?.durationInMinutes) : "N/A",
                decarbPlanQuestionnaireId: foundDecarbQuestionnaire?.questionnaireId ?? null,
                decarbPlanOriginalStatus: foundDecarbQuestionnaire?.status ?? null
              };

              const ghgYears = [2021, 2022, 2023, 2024];
              ghgYears.forEach(year => {
                const foundGHGQuestionnaire = data[supplier][username].find(
                  questionnaire => questionnaire.type !== SUPPLIER_QUESTIONNAIRE_CATEGORIES.DECARBONIZATION &&
                    questionnaire.year === year
                );

                if (typeof foundGHGQuestionnaire !== "undefined") {
                  objToPush[`ghgStatus${year}`] = getStatusLabel(
                    foundGHGQuestionnaire?.status, foundGHGQuestionnaire?.durationInMinutes
                  );
                  objToPush[`ghgType${year}`] = foundGHGQuestionnaire?.type;
                  objToPush[`ghgQuestionnaireId${year}`] = foundGHGQuestionnaire?.questionnaireId;
                  objToPush[`ghgOriginalStatus${year}`] = foundGHGQuestionnaire?.status;
                } else {
                  objToPush[`ghgStatus${year}`] = "N/A";
                  objToPush[`ghgQuestionnaireId${year}`] = null;
                  objToPush[`ghgOriginalStatus${year}`] = null;
                }
              });

              mappedData.push(objToPush);
            });
          }
        });
      }

      setReportsData(mappedData);
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    cacheTime: 0
  });

  const columns = useMemo(() => {
    function handleViewQuestionnaire(recordId) {
      setSelectedQuestionnaireId(recordId);
      onSuppliersQuestionnaireOpen();
    }

    function getGHGStatusColumn(year) {
      return {
        header: t("headers.ghgStatus", { year: year }),
        accessorKey: `ghgStatus${year}`,
        size: 80,
        cell: cell => <StatusBadge cellObj={cell.row.original} year={year} onClickHandler={handleViewQuestionnaire} />,
        enableColumnFilter: false
      };
    }

    return [
      {
        header: t("headers.supplierName"),
        accessorKey: "supplier",
        size: 120
      },
      {
        header: t("headers.username"),
        accessorKey: "username",
        size: 100
      },
      {
        header: t("headers.decarbPlan"),
        accessorKey: "decarbPlan",
        size: 80,
        cell: (el) => {
          const cell = el.row.original;
          const isReviewable = cell.decarbPlanQuestionnaireId &&
            (cell.decarbPlanOriginalStatus &&
              [QUESTIONNAIRE_STATUSES.IN_PROGRESS, QUESTIONNAIRE_STATUSES.FINALIZED].includes(cell.decarbPlanOriginalStatus)
            );

          return (
            <Text
              textDecoration={isReviewable ? "underline" : "none"}
              cursor={isReviewable ? "pointer" : "auto"}
              title={isReviewable ? t("labels.reviewDecarbPlan") : ""}
              onClick={isReviewable ? () => handleViewQuestionnaire(cell.decarbPlanQuestionnaireId) : null}
            >
              {cell.decarbPlan ?? ""}
            </Text>
          );
        },
        enableColumnFilter: false,
      },
      getGHGStatusColumn(2021),
      getGHGStatusColumn(2022),
      getGHGStatusColumn(2023),
      getGHGStatusColumn(2024)
    ];
  }, [t, onSuppliersQuestionnaireOpen]);

  return (
    <Box>
      <DataTable
        data={reportsData}
        columns={columns}
        isLoading={isReportsDataFetching}
        showPaginator={false}
        queryPageSize={2000}
        enableFilters={true}
      />
      <SuppliersDataModal
        questionnaireId={selectedQuestionnaireId}
        isOpen={isSuppliersQuestionnaireOpen}
        onClose={closeSuppliersQuestionnaireModal}
      />
    </Box>
  );
}

export const getStatusLabel = (status, duration) => {
  if (status === QUESTIONNAIRE_STATUSES.IN_PROGRESS) {
    return "In Progress";
  } else if (status === QUESTIONNAIRE_STATUSES.FINALIZED) {
    if (duration) {
      const momentDuration = moment.duration(duration, "minutes");
      return `Finalised - ${momentDuration.days()}d.`;
    } else {
      return "Finalised";
    }
  } else {
    return "New";
  }
};

export default SupplierEngagementGrid;